import { type SortingState } from '@tanstack/react-table'
import { useState } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import NeedsAttentionContext from 'components/context/NeedsAttentionContext'
import { getBrokersMissingPaymentsQueryKey } from 'components/hooks/useGetBrokersMissingPayments'
import { Button } from 'slab'
import { type BrokersBroker } from 'utils/web_api.schemas'

import CreateOrEditNAVItem, {
  type CreateOrEditNAVOptionsState,
} from './CreateOrEditNAVItem'
import {
  defaultFilters,
  type NeedsAttentionPagination,
  NeedsAttentionTableImpl,
} from './NeedsAttentionTable'

type NeedsAttentionMemberViewProps = {
  memberId: string
  isNAVAdmin: boolean
  currentBroker: BrokersBroker
}

export const defaultVisibilityOverrides = {
  silence: true,
  resolve: true,
  openInNewTab: true,
}

const NeedsAttentionMemberView = ({
  memberId,
  isNAVAdmin,
  currentBroker,
}: NeedsAttentionMemberViewProps) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState<NeedsAttentionPagination>({
    pageIndex: 1,
    pageSize: 5,
  })
  const [createOrEditNAVItemOptions, setCreateOrEditNAVItemOptions] =
    useState<CreateOrEditNAVOptionsState>({
      isOpen: false,
      item: undefined,
    })

  const missingPaymentsArgs = {
    sorting,
    pagination,
    filters: {
      ...defaultFilters,
      member_ids: { include: [memberId], exclude: [] },
    },
  }

  return (
    <NeedsAttentionContext.Provider
      value={{
        isNAVAdmin,
        // Only those with this flag enabled will see this component, no need to pass as prop.
        isNAVv2ToolingEnabled: true,
        getBrokersMissingPaymentsQueryKey:
          getBrokersMissingPaymentsQueryKey(missingPaymentsArgs),
        actionsVisibilityOverrides: {
          silence: true,
          resolve: true,
          openInNewTab: false,
        },
        createOrEditNAVItemOptions,
        setCreateOrEditNAVItemOptions,
      }}
    >
      <BrokerContext.Provider value={{ currentBroker }}>
        <CreateOrEditNAVItem
          memberId={memberId}
          isOpen={createOrEditNAVItemOptions.isOpen}
          item={createOrEditNAVItemOptions.item}
          setIsOpen={setCreateOrEditNAVItemOptions}
        />
        <div className='flex items-center justify-between mb-4'>
          <div className='h2'>Needs Attention Items</div>
          <div>
            <Button
              variant='primary'
              onClick={() =>
                setCreateOrEditNAVItemOptions((prev) => ({
                  ...prev,
                  isOpen: true,
                  item: undefined,
                }))}
            >
              Create Manual Item
            </Button>
          </div>
        </div>
        <NeedsAttentionTableImpl
          missingPaymentsArgs={missingPaymentsArgs}
          setSorting={setSorting}
          setPagination={setPagination}
          // We don't want rows to be selectable in this view.
          rowSelection={{}}
          setRowSelection={() => {}}
          setSettings={() => {}}
          columnVisibilityOverrides={{
            employee: false,
            submission_method: false,
            category: false,
            state: false,
            amount_expected: false,
            brokerage_name: false,
            employer_name: false,
            amount_paid: false,
            select: false,
          }}
        />
      </BrokerContext.Provider>
    </NeedsAttentionContext.Provider>
  )
}

export default NeedsAttentionMemberView
